import cx from 'classnames'
import React, { FC, useCallback, useEffect, useRef, useState } from 'react'
import { motionSlide } from 'src/hooks/useAppear'
import { colors } from 'src/theme'
import { useKeenSlider } from 'keen-slider/react'
import 'keen-slider/keen-slider.min.css'

import { BlockTitleProps } from '../BlockTitle'
import { Icons } from '../Icon'
import { VideoYoutubeProps } from '../VideoYoutube'

import * as SC from './styled'

export type VideoPushProps = {
  className?: string
  blockTitleProps?: BlockTitleProps
  thumbnails?: {
    title?: string
    htmlTitleTag?: React.ElementType
  }
  videos?: (VideoYoutubeProps & {
    title?: string
    htmlTitleTag?: React.ElementType
  })[]
  color?: string
  inEdito?: boolean
  htmlTag?: React.ElementType
  texts?: {
    dot?: string
    previous?: string
    next?: string
  }
}

const VideoPush: FC<VideoPushProps> = ({
  className,
  videos,
  blockTitleProps,
  thumbnails,
  color = colors.blueLight,
  htmlTag,
  texts,
}) => {
  const [currentVideo, setCurrentVideo] = useState<number>(0)
  const [openTranscript, setOpenTranscript] = useState(false)
  const refTranscript = useRef<HTMLDivElement>(null)

  const toggleTranscript = useCallback(
    (scroll?: boolean) => {
      const newState = !openTranscript
      setOpenTranscript(newState)
      const target = refTranscript.current?.offsetTop
      if (target && scroll && newState) {
        setTimeout(function () {
          window.scrollTo({
            top: target - 100,
            left: 0,
            behavior: 'smooth',
          })
        }, 150)
      }
    },
    [openTranscript]
  )

  const skip = useCallback(() => {
    if (!videos) return
    if (currentVideo < videos.length - 1) {
      setCurrentVideo(currentVideo + 1)
    } else {
      setCurrentVideo(0)
    }
  }, [currentVideo, videos])

  useEffect(() => {
    if (currentVideo) setOpenTranscript(false)
  }, [currentVideo])

  const { motionProps } = motionSlide({})

  // slider
  const [currentSlide, setCurrentSlide] = useState(0)
  const [loaded, setLoaded] = useState(false)
  const [sliderRef, instanceRef] = useKeenSlider({
    drag: true,
    slides: { perView: 1.5, spacing: 16 },
    defaultAnimation: {
      duration: 300,
    },
    breakpoints: {
      // 600 as in sizes
      '(min-width: 37.5em)': {
        slides: { perView: 2.15, spacing: 16 },
      },
      // 900 as in sizes
      '(min-width: 56.25em)': {
        slides: { perView: 4, spacing: 24 },
      },
    },
    slideChanged(slider) {
      setCurrentSlide(slider.track.details.rel)
    },
    created() {
      setLoaded(true)
    },
  })

  // change slide
  const handleSlideChange = useCallback(
    (slide: number) => {
      if (loaded && instanceRef.current) {
        instanceRef.current?.moveToIdx(slide)
      }
    },
    [instanceRef, loaded]
  )

  return videos && videos.length > 0 ? (
    <SC.Container
      className={cx('VideoPush', className)}
      data-single={videos.length === 1}
      $color={color}
      as={htmlTag}
    >
      {blockTitleProps && <SC._BlockTitle {...blockTitleProps} color={color} />}
      <SC.Wrapper {...motionProps}>
        <SC.Content>
          <SC.Video role="tabpanel" aria-labelledby={`tab-${currentVideo}`}>
            <SC._VideoYoutube
              {...videos[currentVideo]}
              color={color}
              handleSkip={skip}
              handleTranscript={() => toggleTranscript(true)}
            />
          </SC.Video>
          {videos && videos?.length > 1 && (
            <>
              <SC.Thumbnails>
                {thumbnails?.title && (
                  <>
                    <SC.ThumbnailsTitle as={thumbnails.htmlTitleTag}>
                      {thumbnails?.title}
                    </SC.ThumbnailsTitle>
                    <SC._Separator role="separator" aria-hidden />
                  </>
                )}
                {/* 
                <SC._Slider
                  role="tablist"
                  keenOptions={{
                    drag: true,
                    slides: { perView: 1.5, spacing: 24 },
                    defaultAnimation: {
                      duration: 300,
                    },
                    breakpoints: {
                      // 750 as in sizes
                      '(min-width: 46.875em)': {
                        slides: { perView: 2.5, spacing: 24 },
                      },
                      // 900 as in sizes
                      '(min-width: 56.25em)': {
                        slides: { perView: 4, spacing: 24 },
                      },
                    },
                  }}
                  slides={videos.map((video, index) => {
                    return {
                      node: (
                        <SC.Tab
                          role="tab"
                          tabIndex={0}
                          aria-selected={currentVideo === index}
                          onClick={() => setCurrentVideo(index)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              setCurrentVideo(index)
                            }
                          }}
                        >
                          <SC.Thumbnail>
                            <SC.ThumbnailPicture {...video.cover} />
                            <SC.ThumbnailPlay>
                              <SC.ThumbnailIcon icon={Icons.playerPlay} />
                            </SC.ThumbnailPlay>
                          </SC.Thumbnail>
                          <SC.ThumbnailTitle as={video.htmlTitleTag}>
                            {video.title}
                          </SC.ThumbnailTitle>
                        </SC.Tab>
                      ),
                    }
                  })}
                />
                */}
                <SC.Slider>
                  <SC.Keen
                    className="keen-slider"
                    ref={sliderRef}
                    role="tablist"
                  >
                    {videos.map((video, index) => (
                      <SC.Slide key={index} className="keen-slider__slide">
                        <SC.Tab
                          role="tab"
                          tabIndex={0}
                          aria-selected={currentVideo === index}
                          onClick={() => setCurrentVideo(index)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              setCurrentVideo(index)
                            }
                          }}
                        >
                          <SC.Thumbnail>
                            <SC.ThumbnailPicture {...video.cover} />
                            <SC.ThumbnailPlay>
                              <SC.ThumbnailIcon icon={Icons.playerPlay} />
                            </SC.ThumbnailPlay>
                          </SC.Thumbnail>
                          <SC.ThumbnailTitle as={video.htmlTitleTag}>
                            {video.title}
                          </SC.ThumbnailTitle>
                        </SC.Tab>
                      </SC.Slide>
                    ))}
                  </SC.Keen>
                  <SC.SliderNav>
                    <SC.SliderDots>
                      {videos.map((_card, index) => (
                        <SC.SliderDot
                          key={index}
                          onClick={() => handleSlideChange(index)}
                          {...(index === currentSlide
                            ? { 'aria-current': true }
                            : {})}
                        >
                          <span>
                            {texts?.dot} {index}
                          </span>
                        </SC.SliderDot>
                      ))}
                    </SC.SliderDots>
                    <SC.SliderArrows>
                      <SC.SliderArrow
                        type="button"
                        onClick={() => handleSlideChange(currentSlide - 1)}
                        disabled={currentSlide === 0}
                      >
                        <SC.SliderArrowIcon
                          icon={Icons.chevronLeft}
                          title={texts?.previous}
                        />
                      </SC.SliderArrow>
                      <SC.SliderNumerotation>
                        {(currentSlide + 1).toString().padStart(2, '0')} —{' '}
                        {videos.length.toString().padStart(2, '0')}
                      </SC.SliderNumerotation>
                      <SC.SliderArrow
                        type="button"
                        onClick={() => {
                          handleSlideChange(currentSlide + 1)
                          console.log(instanceRef.current)
                        }}
                        disabled={
                          currentSlide >=
                          (instanceRef.current?.track.details.maxIdx ??
                            Infinity)
                        }
                      >
                        <SC.SliderArrowIcon
                          icon={Icons.chevronRight}
                          title={texts?.next}
                        />
                      </SC.SliderArrow>
                    </SC.SliderArrows>
                  </SC.SliderNav>
                </SC.Slider>
              </SC.Thumbnails>
              <SC.Decoration aria-hidden />
            </>
          )}
        </SC.Content>
        {videos[currentVideo].transcriptProps && (
          <SC._Transcript
            ref={refTranscript}
            {...videos[currentVideo].transcriptProps}
            texts={
              videos[currentVideo].transcriptProps?.texts || {
                open: 'open',
                close: 'close',
              }
            }
            toggleHandler={toggleTranscript}
            open={openTranscript}
          />
        )}
      </SC.Wrapper>
    </SC.Container>
  ) : null
}

export default VideoPush
